import React from "react";
import { AppStoreStatus, useAppStoreStatus } from "../lib/appStore";
import "./AppStoreBadge.css";
import { t } from "../i18n";

// Apple mark (same path as PlaylistPage's AppleMark — kept local so the
// landing page doesn't import a whole page module for one glyph).
export const AppleGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
    />
  </svg>
);

interface Props {
  // Normally read from the server (lib/appStore). Passing it explicitly lets
  // a parent that already holds the status reuse it.
  status?: AppStoreStatus;
  label?: string;
  className?: string;
  onClick?: () => void;
}

// The black App Store pill. Renders NOTHING until the listing is live — so it
// appears on its own the day Apple publishes the app (and never inside the
// native app, which is the app).
const DEFAULT_LABEL = t("appStoreBadge.downloadOnTheApp");

const AppStoreBadge: React.FC<Props> = ({ status, label = DEFAULT_LABEL, className = "", onClick }) => {
  const fetched = useAppStoreStatus();
  const s = status ?? fetched;
  if (!s.live || !s.url) return null;
  return (
    <a
      className={`appstore-badge ${className}`.trim()}
      href={s.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      aria-label={label}
    >
      <AppleGlyph />
      {label === DEFAULT_LABEL ? (
        // Apple's own badge layout: a small line over a bold one. One line
        // needed ~260px and the hero column is 240px, so it spilled out of
        // the button on phones and narrow desktops.
        <span className="appstore-badge-text appstore-badge-text--stacked">
          <span className="appstore-badge-small">{t("appStoreBadge.downloadOnThe")}</span>
          <span className="appstore-badge-big">{t("appStoreBadge.appStore")}</span>
        </span>
      ) : (
        <span className="appstore-badge-text">{label}</span>
      )}
    </a>
  );
};

export default AppStoreBadge;
